import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { EquipmentContent, EquipmentFilters, EquipmentResponse } from 'src/app/domains/equipment/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { Company } from 'src/app/domains/companies/models';
import { EquipmentService } from 'src/app/domains/equipment/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { AddEquipmentPopupComponent } from 'src/app/features/equipment/components/add-equipment-popup/add-equipment-popup.component';

const PAGE_SIZE = 30;

@Component({
  selector: 'gdp-company-equipment',
  templateUrl: './company-equipment.component.html',
  styleUrls: ['./company-equipment.component.scss']
})
export class CompanyEquipmentComponent implements OnInit, OnDestroy {
  @Input() company: Company;
  private routerSubscription: Subscription;
  public filters: EquipmentFilters = null;
  public lockedFilters = ['alphaname','pbxId'];
  public limitedAvailableFilters = ['model'];
  public pageSize: number;
  public equipmentList: Array<EquipmentContent> = [];
  public loading: boolean = false;
  public loadingLines: Array<number>;
  public sameFilters: boolean = true;
  public equipmentMacaddress: string = '';
  public pagination = {
    page: 0,
    empty: true,
    totalPages: 0
  };
  public companyBLF: boolean;
  public loadingBLF = false;

  constructor(
    private equipmentService: EquipmentService,
    private notification: NotificationService,
    private modalService: NgbModal,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.pageSize = PAGE_SIZE;
    this.filters = {
      model: null,
      alphaname: this.company.alphaname,
      pbxId: this.company.pbxId,
      macAddress: null,
      ipui: null
    };
    this.getEquipment();

    if (this.route.snapshot.params && this.route.snapshot.params.mac) {
      this.equipmentMacaddress = this.route.snapshot.params.mac;
    }
    this.routerSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e:NavigationEnd) => {
        if (this.route.firstChild !== null && this.route.firstChild.snapshot.params.mac) {
          this.equipmentMacaddress = this.route.firstChild.snapshot.params.mac;
        } else {
          this.equipmentMacaddress = '';
        }
      });
  }

  filterChanged(event:EquipmentFilters) {
    if (this.areNotEmptyFilters(event)) {
        this.filters = event;
      } else {
        this.filters = null;
      }
    this.sameFilters = false;
    this.getEquipment();
  }

  areNotEmptyFilters(event) {
    return event.macAddress !== '' ||
    event.alphaname !== '' ||
    event.model !== '' ||
    event.pbxId !== '';
  }

  selectPageSize(event) {
    this.pageSize = event;
    this.pagination.page = 0;
    this.getEquipment();
  }

  changeCurrentPage(event) {
    this.pagination = {
      page: event,
      empty: this.equipmentList.length === 0,
      totalPages: this.pagination.totalPages
    }
    this.getEquipment();
  }

  setLoadingLines() {
    this.loadingLines = [];
    for(var i = 1; i <= this.pageSize; i++) {
      this.loadingLines.push(i);
    }
  }

  updateBlf(equipment: EquipmentContent[]) {
    // there aren't a lot of equipment, so we can go through it twice
    const allTrue = equipment.every(e => e.blf);
    const allFalse = equipment.every(e => !e.blf);
    if (allTrue) {
      this.companyBLF = true;
    }
    if (allFalse) {
      this.companyBLF = false;
    }
    // leave undefined otherwise
  }

  getEquipment() {
    this.setLoadingLines();
    this.loading = true;
    if(this.filters && this.filters.macAddress) {
      this.equipmentService
        .getEquipmentByMacAddress(this.filters.macAddress,
          [
            {
              status: 404,
              message: 'equipment.filters.errors.macAddress-not-found'
            },
            {
              status: 500,
              message: 'equipment.filters.errors.macAddress-invalid'
            }
          ])
        .subscribe(
          (res: EquipmentContent) => {
            this.equipmentList = [res];
            this.pagination.totalPages = 1;
            this.loading = false;
          }, () => {
            this.equipmentList = [];
            this.pagination.totalPages = 1;
            this.pagination.page = 0;
            this.loading = false;
          }
        );
    } else if(this.filters && this.filters.ipui) {
      this.equipmentService
        .getEquipmentByIpui(this.filters.ipui,
          [
            {
              status: 404,
              message: 'equipment.filters.errors.ipui-not-found',
            },
            {
              status: 500,
              message: 'equipment.filters.errors.ipui-invalid'
            }
          ])
        .subscribe(
          (res: EquipmentContent) => {
            this.equipmentList = [res];
            this.pagination.totalPages = 1;
            this.loading = false;
          }, () => {
            this.equipmentList = [];
            this.pagination.totalPages = 1;
            this.pagination.page = 0;
            this.loading = false;
          }
        );
    } else {
      if (this.filters && !this.sameFilters) {
        this.pagination.page = 0;
      } else {
        this.pagination.page = this.pagination.page;
      }
      this.sameFilters = true;
      this.equipmentService
        .getEquipment(
          this.pagination.page,
          this.pageSize,
          this.filters
        )
        .subscribe(
          (res: EquipmentResponse) => {
            this.equipmentList = res.content;
            this.updateBlf(res.content);
            this.equipmentList.length !== 0 ? this.pagination.totalPages = res.totalPages : this.pagination.totalPages = 1;
            this.loading = false;
          }, () => {
            this.loading = false;
          }
        );
    }
  }

  openAddEquipmentPopup() {
    const modal = this.modalService.open(
      AddEquipmentPopupComponent,
      { size: 'lg' }
    );
    const control = modal.componentInstance.form.get('company');
    control.setValue(this.company);
    control.disable();
    modal.componentInstance
      .onAddEquipment
      .pipe(take(1))
      .subscribe(() => { this.getEquipment(); });
  }

  setBlf() {
    this.loadingBLF = true;
    this.equipmentService.setPbxBlf(this.filters.pbxId, this.companyBLF)
      .subscribe(
        response => {
          this.loadingBLF = false;
          this.notification.success('company.equipment.pbx-blf-success');
        }, () => {
          this.loadingBLF = false;
        }
      )
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
  }

}
